import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Droplets,
  LayoutDashboard,
  Users,
  UserCircle,
  Calendar,
  Package,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  CreditCard,
  Bell,
  FileEdit,
  MessageCircle,
  BarChart3,
  ClipboardPlus,
  TrendingUp,
  Route,
  BookOpen,
  Receipt
} from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/new-report', label: 'Nuevo parte', icon: ClipboardPlus },
  { path: '/workers', label: 'Trabajadores', icon: Users },
  { path: '/clients', label: 'Clientes', icon: UserCircle },
  { path: '/calendar', label: 'Calendario', icon: Calendar },
  { path: '/routes', label: 'Rutas', icon: Route },
  { path: '/materials', label: 'Materiales', icon: Package },
  { path: '/templates', label: 'Plantillas', icon: FileEdit },
  { path: '/reports', label: 'Reportes', icon: FileText },
  { path: '/quotes', label: 'Facturación', icon: Receipt },
  { path: '/analytics', label: 'Análisis', icon: TrendingUp },
  { path: '/summary', label: 'Resumen', icon: BarChart3 },
  { path: '/chat', label: 'Asistente', icon: MessageCircle },
  { path: '/guide', label: 'Guía', icon: BookOpen },
];

export const OwnerLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 h-screen border-r bg-white fixed left-0 top-0 z-30 flex-col pointer-events-auto">
        <div className="p-6 border-b flex-shrink-0">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-600 flex items-center justify-center">
              <Droplets className="w-6 h-6 text-white" />
            </div>
            <span className="font-display font-bold text-xl text-slate-900">Aqua Nexia</span>
          </Link>
        </div>
        
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive 
                    ? 'bg-sky-100 text-sky-900' 
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>
        
        <div className="p-4 border-t space-y-1 flex-shrink-0">
          <Link
            to="/subscription"
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              location.pathname === '/subscription' 
                ? 'bg-sky-100 text-sky-900' 
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            <CreditCard className="w-5 h-5" />
            <span className="font-medium">Suscripción</span>
          </Link>
          <Link
            to="/settings"
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              location.pathname === '/settings' 
                ? 'bg-sky-100 text-sky-900' 
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            <Settings className="w-5 h-5" />
            <span className="font-medium">Configuración</span>
          </Link>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b z-50 flex items-center justify-between px-4">
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-sky-600 flex items-center justify-center">
            <Droplets className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-bold text-lg">Aqua Nexia</span>
        </Link>
        
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => setSidebarOpen(true)}
          data-testid="mobile-menu-btn"
          className="touch-manipulation"
        >
          <Menu className="w-6 h-6" />
        </Button>
      </header>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="md:hidden fixed inset-0 bg-black/50 z-[60]"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="md:hidden fixed left-0 top-0 w-72 bg-white z-[70] h-screen"
            >
              {/* Header - Fixed at top */}
              <div className="absolute top-0 left-0 right-0 p-4 border-b flex items-center justify-between bg-white z-10">
                <Link to="/dashboard" onClick={() => setSidebarOpen(false)} className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-sky-600 flex items-center justify-center">
                    <Droplets className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-display font-bold text-lg">Aqua Nexia</span>
                </Link>
                <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              {/* Scrollable Navigation Area - with explicit positioning */}
              <div 
                className="absolute left-0 right-0 overflow-y-scroll"
                style={{ 
                  top: '65px',
                  bottom: 0,
                  WebkitOverflowScrolling: 'touch',
                  overscrollBehavior: 'contain',
                  touchAction: 'pan-y'
                }}
              >
                <nav className="p-4 space-y-1">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                          isActive 
                            ? 'bg-sky-100 text-sky-900' 
                            : 'text-slate-600 hover:bg-slate-100 active:bg-slate-200'
                        }`}
                      >
                        <Icon className="w-5 h-5 flex-shrink-0" />
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    );
                  })}
                </nav>
                
                {/* Bottom Links - Inside scroll area */}
                <div className="p-4 border-t space-y-1">
                  <Link
                    to="/subscription"
                    onClick={() => setSidebarOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-600 hover:bg-slate-100 active:bg-slate-200"
                  >
                    <CreditCard className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium">Suscripción</span>
                  </Link>
                  <Link
                    to="/settings"
                    onClick={() => setSidebarOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-600 hover:bg-slate-100 active:bg-slate-200"
                  >
                    <Settings className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium">Configuración</span>
                  </Link>
                  <button
                    onClick={() => { setSidebarOpen(false); handleLogout(); }}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 active:bg-red-100 w-full"
                  >
                    <LogOut className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium">Cerrar sesión</span>
                  </button>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="md:ml-64 pt-16 md:pt-0 min-h-screen">
        {/* Top Bar */}
        <div className="hidden md:flex h-16 border-b bg-white items-center justify-between px-8">
          <h1 className="font-display font-semibold text-lg text-slate-900">
            {navItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
          </h1>
          
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="w-5 h-5" />
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2">
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-sky-100 text-sky-700 text-sm">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="font-medium text-sm">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="w-4 h-4 mr-2" />
                  Configuración
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        
        <div className="p-4 md:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default OwnerLayout;
